/**
 * People being brought out (`world.objective.captives`): POWs, hostages, the captured
 * officer. `held` captives are in the pits or the prison and are not drawn; once `following`
 * they walk at the ranger's heels; `boarded` and `lost` are gone from the ground. A contact who is
 * `waiting` at the rendezvous (10 §2, the defector) stands there to be met; `absent`, he is not there yet.
 *
 * They use the soldier rig with nothing on the weapon bone. The officer - and the scientist, who is
 * taken the same way - walks with his hands up (the `handsUp` overlay over whatever his legs are
 * doing); everyone goes up the rope ladder hand over hand while the sim counts his `climbTicks`.
 */

import type { Material, Scene } from 'three';
import { isPrisoner } from '../../sim/objectives';
import { OBJECTIVES } from '../../sim/tuning';
import { type Captive, TICK_RATE, type World } from '../../sim/types';
import type { PixelCamera } from '../camera/PixelCamera';
import { Soldier, type SoldierLook } from '../characters/soldier';
import type { Palette } from '../palette';
import { HOVER_HEIGHT } from './OspreyView';

const SKINS = ['skinA', 'skinB', 'skinC'] as const;
/** How far up the ladder a man is when the sim counts him aboard. */
const LADDER_TOP = HOVER_HEIGHT - 1.6;

/** What each kind of captive wears; characters/model.ts describes the expansion's three. Exported for the tests. */
export function captiveLook(c: Pick<Captive, 'id' | 'kind'>): SoldierLook {
  const skin = SKINS[c.id % SKINS.length] ?? 'skinA';
  const plain = { skin, pack: false, rifle: false } as const;
  switch (c.kind) {
    // The officer keeps his cap and boards: he must stay recognisable all the way to the ladder.
    case 'officer': return { ...plain, side: 'officer', headgear: 'cap' };
    case 'scientist': return { ...plain, side: 'scientist', headgear: 'none' };
    case 'pilot': return { ...plain, side: 'pilot', headgear: 'flightHelmet' };
    case 'defector': return { ...plain, side: 'defector', headgear: 'cap' };
    default: return { ...plain, side: 'captive', headgear: 'none' };
  }
}

export class Captives {
  private readonly soldiers = new Map<number, Soldier>();

  constructor(
    private readonly scene: Scene,
    private readonly palette: Palette,
    /** Character outline class: they are people, and not the player. */
    private readonly material: Material,
    private readonly groundY: (x: number, z: number) => number,
  ) {}

  update(world: World, time: number, alpha: number, camera: PixelCamera): void {
    for (const c of world.objective.captives) {
      let soldier = this.soldiers.get(c.id);
      if (c.state !== 'following' && c.state !== 'waiting') {
        if (soldier) soldier.mesh.visible = false;
        continue;
      }
      if (!soldier) {
        // Built when first freed: most missions have no captives at all.
        soldier = new Soldier(this.palette, this.material, captiveLook(c));
        this.soldiers.set(c.id, soldier);
        this.scene.add(soldier.mesh);
      }
      soldier.mesh.visible = true;
      const s = soldier.state;
      soldier.clearOverlays();
      if (c.climbTicks > 0) {
        // On the ladder under the ramp, back to the camera, a second from the ground to the lip.
        const k = Math.min(1, c.climbTicks / OBJECTIVES.captiveBoardTicks);
        const x = world.objective.pickupX;
        const z = world.objective.pickupZ + 0.25;
        s.position.set(x, this.groundY(x, z) + k * LADDER_TOP, z);
        s.facing = Math.PI;
        s.motion = 'board';
        s.age = c.climbTicks / TICK_RATE;
      } else {
        const x = c.prevX + (c.x - c.prevX) * alpha;
        const z = c.prevZ + (c.z - c.prevZ) * alpha;
        s.position.set(x, this.groundY(x, z), z);
        s.facing = c.facing;
        // Escorts jog (3.4 m/s) unless hurt; a wounded man can only manage a walk - and the pilot came down
        // hard, so he walks from the start (1.8 m/s in the sim). The rig has no limp, so it is the plain walk.
        s.motion = c.moving ? (c.wounded || c.kind === 'pilot' ? 'walk' : 'run') : 'idle';
        s.travelled = c.travelled;
        s.handsUp = isPrisoner(c.kind);
      }
      soldier.update(time, camera);
    }
  }

  dispose(): void {
    for (const soldier of this.soldiers.values()) {
      this.scene.remove(soldier.mesh);
      soldier.mesh.geometry.dispose();
      soldier.mesh.skeleton.dispose();
    }
    this.soldiers.clear();
  }
}
